import {Checkbox, Group, NumberInput, Stack, Text} from '@mantine/core'
import {useState} from 'react'

import {
    type PickupSelection,
    pickupUnitLabel,
    stockGroupLabel,
    stockGroups,
} from './pickup-selection'
import type {PickupUnit} from './schemas'

type PickupUnitsChecklistProps = {
    units: PickupUnit[]
    selection: PickupSelection
    onChange: (selection: PickupSelection) => void
}

export function PickupUnitsChecklist({units, selection, onChange}: PickupUnitsChecklistProps) {
    const named = units.filter((unit) => !unit.isStock)
    const [cappedGroupKeys, setCappedGroupKeys] = useState<string[]>([])
    return (
        <Stack gap="xs">
            {named.map((unit) => (
                <Checkbox
                    key={unit.rentalAssetId}
                    label={pickupUnitLabel(unit)}
                    description={unit.locationLabel}
                    checked={selection.unitIds.includes(unit.rentalAssetId)}
                    onChange={(event) =>
                        onChange({
                            ...selection,
                            unitIds: event.currentTarget.checked
                                ? [...selection.unitIds, unit.rentalAssetId]
                                : selection.unitIds.filter((id) => id !== unit.rentalAssetId),
                        })
                    }
                />
            ))}
            {stockGroups(units).map((group) => (
                <Stack key={group.key} gap={2}>
                    <Group gap="sm" wrap="nowrap">
                        <NumberInput
                            aria-label={`${stockGroupLabel(group)} to pick up`}
                            w={90}
                            min={0}
                            max={group.units.length}
                            allowDecimal={false}
                            value={selection.stockCounts[group.key] ?? 0}
                            onChange={(value) => {
                                const typed = Math.max(0, Number(value) || 0)
                                const capped = typed > group.units.length
                                setCappedGroupKeys((ids) =>
                                    capped
                                        ? [...new Set([...ids, group.key])]
                                        : ids.filter((id) => id !== group.key),
                                )
                                onChange({
                                    ...selection,
                                    stockCounts: {
                                        ...selection.stockCounts,
                                        [group.key]: Math.min(group.units.length, typed),
                                    },
                                })
                            }}
                        />
                        <Text size="sm">
                            of {group.units.length} {stockGroupLabel(group)} still out
                        </Text>
                    </Group>
                    {cappedGroupKeys.includes(group.key) ? (
                        <Text size="xs" c="orange">
                            Only {group.units.length} {stockGroupLabel(group)} are still out.
                        </Text>
                    ) : null}
                </Stack>
            ))}
        </Stack>
    )
}
